import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import type { FinanceAction } from "@bw/shared/types/generated";
import { useSelectedOrganization } from "@/app/OrganizationContext";
import { clearPersistedFacilitySelection } from "@/app/FacilityContext";
import { acceptStaffInvitation, getStaffInvitation } from "@/libs/staffApi";

const financeLabels: Record<FinanceAction, string> = {
  "org:finance_read": "View financial reports",
  "org:finance_billing": "Manage company billing",
  "org:finance_disputes": "Manage payment disputes",
  "org:finance_repair": "Repair payment operations",
};

export function StaffInvitationPage() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const { user } = useAuth();
  return <InvitationReview key={JSON.stringify([user?.uid, invitationId])} invitationId={invitationId ?? ""} />;
}

function InvitationReview({ invitationId }: { invitationId: string }) {
  const { user } = useAuth();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { selectOrganization } = useSelectedOrganization();
  const queryKey = ["staff", "invitation", invitationId, user?.uid];
  const query = useQuery({ queryKey, queryFn: () => getStaffInvitation(invitationId), enabled: Boolean(invitationId && user), retry: false });
  const command = useRef<{ key: string; revision: number } | null>(null);
  const busy = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invitation = query.data;
  const additional = invitation?.additional_access;
  const completeReview = Boolean(additional &&
    Array.isArray(additional.organization_roles) && Array.isArray(additional.groups) &&
    Array.isArray(additional.finance_actions));

  const accept = async () => {
    if (!invitation?.can_accept || !completeReview || busy.current) return;
    busy.current = true;
    setSubmitting(true);
    setError(null);
    // A transport failure retains the exact intent/key for retry.
    command.current ??= { key: crypto.randomUUID(), revision: invitation.revision };
    try {
      const result = await acceptStaffInvitation(invitationId, command.current.revision, command.current.key);
      client.setQueryData(queryKey, result);
      void client.invalidateQueries({ queryKey: ["org", "me"] });
      void client.invalidateQueries({ queryKey: ["staff", "my-invitations"] });
    } catch {
      setError("We couldn’t confirm that you joined. You can retry the same request.");
    } finally {
      busy.current = false;
      setSubmitting(false);
    }
  };

  return <Stack component="main" spacing={3} sx={{ maxWidth: 600, mx: "auto", py: { xs: 2, md: 5 } }}>
    <Button component={Link} to="/workspaces" sx={{ alignSelf: "flex-start" }}>Back to your workspaces</Button>
    <Typography component="h1" variant="h4">Organization invitation</Typography>
    {query.isPending ? <Stack role="status" direction="row" spacing={1.5}><CircularProgress size={20} /><Typography>Loading invitation…</Typography></Stack>
      : query.isError || !invitation ? <Alert severity="error" action={<Button onClick={() => void query.refetch()}>Retry</Button>}>
        We couldn’t load this invitation. Use the BW account invited by the organization and verify its email before trying again.
      </Alert> : <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2.5}>
          <Typography component="h2" variant="h5" sx={{ overflowWrap: "anywhere" }}>{invitation.organization_name}</Typography>
          <Typography color="text.secondary">Join with your existing BW account. Your personal profile stays yours, including if you leave this organization.</Typography>
          <List disablePadding aria-label="Invited responsibilities">
            {invitation.duties.map(duty => <ListItem key={`${duty.facility_id}:${duty.role_id}`} disableGutters>
              <ListItemText primary={duty.role_name} secondary={duty.facility_name} />
            </ListItem>)}
            {invitation.staff_management_enabled ? <ListItem disableGutters><ListItemText primary="Manage staff" secondary="Organization-wide team management" /></ListItem> : null}
          </List>
          {invitation.additional_access?.organization_roles?.length ? <Stack spacing={1}>
            <Typography component="h3" variant="subtitle1">Organization roles</Typography>
            {invitation.additional_access.organization_roles.map(role => <Typography key={role.role_id} variant="body2">{role.role_name}</Typography>)}
          </Stack> : null}
          {invitation.additional_access?.groups?.length ? <Stack spacing={1}>
            <Typography component="h3" variant="subtitle1">Groups</Typography>
            {invitation.additional_access.groups.map(group => <Stack key={group.group_id} spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>{group.group_name}</Typography>
              {(group.roles ?? []).map(role => <Typography key={`${role.role_id}:${role.facility_id ?? "organization"}`} variant="body2" color="text.secondary">
                {role.role_name} · {role.scope === "organization" ? "Organization-wide" : role.facility_name}
              </Typography>)}
            </Stack>)}
          </Stack> : null}
          {invitation.additional_access?.finance_actions?.length ? <Stack spacing={1}>
            <Typography component="h3" variant="subtitle1">Financial access</Typography>
            {invitation.additional_access.finance_actions.map(action => <Typography key={action} variant="body2">{financeLabels[action]}</Typography>)}
          </Stack> : null}
          <Typography variant="body2" color="text.secondary">Joining never authorizes a personal card. Company payment methods remain attached to the organization.</Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {invitation.status === "accepted" ? <>
            <Alert severity="success">Invitation accepted.</Alert>
            {invitation.member_status === "active" ? <Button variant="contained" onClick={() => {
              clearPersistedFacilitySelection();
              selectOrganization(invitation.org_id);
              navigate("/", { replace: true });
            }}>Open workspace</Button> : <Alert severity="info">Your organization access is not currently active. Contact the organization if you need access.</Alert>}
          </> : invitation.can_accept && completeReview ? <>
            <Typography variant="caption" color="text.secondary">Expires {new Date(invitation.expires_at).toLocaleString()}</Typography>
            <Button variant="contained" disabled={submitting} onClick={() => void accept()}>{submitting ? "Joining…" : error ? "Retry join" : "Join organization"}</Button>
          </> : <Alert severity="info">{invitation.status === "expired" ? "This invitation has expired. Ask your organization for a new invitation." : invitation.status === "revoked" ? "This invitation was withdrawn." : !completeReview ? "Permission details are unavailable. Refresh before accepting." : "This invitation is not currently available to accept."}</Alert>}
        </Stack>
      </Paper>}
  </Stack>;
}
